/**
 * React Query hooks for DS Coder knowledge-pack management (scope-aware CRUD).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPack,
  deletePack,
  isDsCoderAdmin,
  listPacks,
  updatePack,
} from "@/services/dsCoderPacksService";
import { clearDsCoderPacksCache } from "@/services/dsCoder/packsRepository";
import type { DsCoderPackInput, PackScope } from "@/types/dsCoderPacks";

function packsKey(scope: PackScope, workspaceId?: string | null) {
  return ["ds-coder-packs", scope, scope === "workspace" ? workspaceId ?? null : scope] as const;
}

export function useDsCoderAdmin() {
  return useQuery({
    queryKey: ["ds-coder-admin"],
    queryFn: isDsCoderAdmin,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDsCoderPacks(scope: PackScope, workspaceId?: string | null) {
  return useQuery({
    queryKey: packsKey(scope, workspaceId),
    queryFn: () => listPacks({ scope, workspaceId }),
    enabled: scope !== "workspace" || Boolean(workspaceId),
  });
}

export function useDsCoderPackMutations(scope: PackScope, workspaceId?: string | null) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: packsKey(scope, workspaceId) });
    // Chat-time resolution caches merged packs; drop it so edits apply immediately.
    clearDsCoderPacksCache();
  };

  const create = useMutation({
    mutationFn: (input: DsCoderPackInput) => createPack({ scope, workspaceId, input }),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<DsCoderPackInput> }) =>
      updatePack(id, patch),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deletePack(id),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}
